<script setup lang="ts">
import add_new from '@/assets/add_new.svg'
import { CalendarList } from '@/class/pages/CalendarList'
import { GroupList } from '@/class/pages/GroupList'
import { CalendarService } from '@/class/services/CalendarService'
import { EventNotificationService } from '@/class/services/EventNotificationService'
import SelectMoveTeamModal from '@/components/Settings/SelectMoveTeamModal.vue'
import DeleteConfirmation from '@/components/common/DeleteConfirmation.vue'
import DuplicateConfirmation from '@/components/common/DuplicateConfirmation.vue'
import GeneralConfirmation from '@/components/common/GeneralConfirmation.vue'
import { CalendarType } from '@/models/calendar'
import AppState, {
  fetchUserDetails,
  getCalendarOnboardingStatus,
  getCalendarsInLocation,
  getEquipmentsInLocation,
  getRoomsInLocation,
  getTargetTimezone,
  getTeamsInLocation,
  isCreateCalendarDisabled,
  isCreateGroupDisabled,
  isPermissionsEnabled,
  resetCalendarEditValidationData,
  resetLocationChanged,
  updateCalendarOnboardingStatus,
  updateCalendarStore,
} from '@/states/app'
import { CalendarActionTooltip } from '@/utils/actions'
import { toggleAdvancedView } from '@/utils/commonModalFunctions'
import {
  mountCalendarTypePicker,
  unmountCalendarTypePicker,
} from '@/utils/remoteAppIntegration'
import { formatDuration } from '@/utils/datehelper'
import {
  copyToClipboard,
  generateSlug,
  getCalendarDurationLabels,
  normalizeDurationUnitForApi,
  shouldShowLegacyLink,
} from '@/utils/generalFunctions'
import {
  ArrowRightIcon,
  Copy04Icon,
  Copy07Icon,
  Edit01Icon,
  InfoCircleIcon,
  PlusIcon,
  Share06Icon,
  Toggle03LeftIcon,
  Tool02Icon,
  Trash03Icon,
} from '@gohighlevel/ghl-icons/24/outline'
import {
  renderIcon,
  UIButton,
  UITable,
  UITag,
  UITooltip,
  useNotification,
} from '@gohighlevel/ghl-ui'
import { isEmpty } from 'lodash-es'
import moment from 'moment-timezone'
import {
  computed,
  h,
  nextTick,
  onBeforeUnmount,
  onMounted,
  PropType,
  reactive,
  ref,
  watch,
} from 'vue'
import { useI18n } from 'vue-i18n'
import { CalendarResourceService } from '../../class/services/CalendarResourceService'
import { CALENDAR_RESOURCE_TYPES } from '../../models/calendar_resource'
import { CalendarScopes, ICalendarShare, SHARE_SOURCE } from '../../types'
import { getCalendarTourSteps } from '../../utils/tourSetup'
import EmptyState from '../common/EmptyState.vue'
import FilterItem from '../common/FilterItem.vue'
import SearchBox from '../common/SearchBox.vue'
import Tour from '../common/Tour.vue'
import { useNotify } from '../composition/notification'
import CalendarShareModal from './CalendarShareModal.vue'
const { t } = useI18n()
const { sendNotification } = useNotify()

const props = defineProps({
  openCalendarTypeModal: {
    type: Function,
    required: true,
  },
  openNewGroupModal: {
    type: Function,
    required: true,
  },
  groupFilter: {
    type: String,
    required: false,
  },
  onUpdateGroupFilter: {
    type: Function,
    required: false,
  },
  calendarList: {
    type: CalendarList,
    required: true,
  },
  type: {
    type: String as PropType<'service' | 'sales'>,
    required: false,
  },
  groupList: {
    type: GroupList,
    required: true,
  },
})

const calendarList = props.calendarList
const groupList = props.groupList
const locationId = AppState.locationId
const userId = AppState.loggedInUserDetail?.id
const assignedDataOnly = computed(
  () => AppState.loggedInUserDetail?.permissions?.assignedDataOnly
)

const getOwnedBy = () => {
  const type = props.type
  const key = `calendar_settings_calendar_filter_ownedBy_${locationId}_${userId}_${type}`
  const value = localStorage.getItem(key) || 'anyone'
  calendarList.filters.value.ownedBy = value
  return value
}

const showSelectModal = ref(false)
const calendarRef = ref()

// "+ New Calendar" type picker — a HighRise dropdown mounted from the calendar-meetings MFA as a
// Module-Federation island, anchored under the button. Selecting a preset seeds AppState and opens the
// meetings in create mode (replacing the legacy SelectCalendarTypeModal → CreateCalendarModal flow).
const PICKER_MOUNT_ID = 'CalendarTypePickerMount'
const NEW_CALENDAR_BUTTON_ID = 'calendar-settings-headers-btn-primary'
const showCalendarTypePicker = ref(false)
const pickerMountRef = ref<HTMLElement | null>(null)

const onDocumentPointerDown = (event: Event): void => {
  const target = event.target as Node
  const mountEl = pickerMountRef.value
  const triggerEl = document.getElementById(NEW_CALENDAR_BUTTON_ID)
  const insidePicker = mountEl?.contains(target)
  const onTrigger = triggerEl?.contains(target)
  if (!insidePicker && !onTrigger) {
    closeCalendarTypePicker()
  }
}

const closeCalendarTypePicker = (): void => {
  showCalendarTypePicker.value = false
  document.removeEventListener('pointerdown', onDocumentPointerDown, true)
  // Defer teardown one microtask: onClose fires from inside the island's own handler, so unmounting
  // synchronously would tear down a micro-app mid-dispatch.
  queueMicrotask(() => unmountCalendarTypePicker(PICKER_MOUNT_ID))
}

const openCalendarTypePicker = async (): Promise<void> => {
  if (isCreateCalendarDisabled.value) {
    return
  }
  if (showCalendarTypePicker.value) {
    closeCalendarTypePicker()
    return
  }
  resetCalendarEditValidationData()
  showCalendarTypePicker.value = true
  // Registered before the await, not after: closeCalendarTypePicker() can run while the island is still
  // loading, and it has to be able to remove this listener again. Registering afterwards leaks a
  // document-level listener bound to an already-closed (or unmounted) picker.
  document.addEventListener('pointerdown', onDocumentPointerDown, true)
  await nextTick()
  if (!showCalendarTypePicker.value) {
    return
  }
  try {
    await mountCalendarTypePicker(PICKER_MOUNT_ID, {
      locale: AppState.locale,
      showUseAi: AppState.isCalendarAiEnabled,
      onSelect: (calendarType: string | null) => onPickerSelect(calendarType),
      onUseAi: () => onPickerUseAi(),
      onClose: () => closeCalendarTypePicker(),
    })
  } catch (error) {
    // Remote unreachable / missing export: close the empty dropdown rather than leaving it hanging open.
    console.error('Calendar app: failed to mount the calendar type picker remote', error)
    closeCalendarTypePicker()
  }
}

const startCalendarCreation = (): void => {
  // Service tables keep their own create flow; the type picker is for meetings calendars.
  if (AppState.isMergeCalendarTypeSetupEnabled && props.type !== 'service') {
    void openCalendarTypePicker()
    return
  }
  props.openCalendarTypeModal()
}

// "Use AI" entry in the picker. The Calendar AI assistant is mounted by the platform shell (spm-ts top
// bar), outside this Postmate iframe, so the click is forwarded to the parent rather than handled here.
const onPickerUseAi = (): void => {
  closeCalendarTypePicker()
  AppState.parentApp?.then(parent => {
    parent.emit('open-calendar-ai', {})
  })
}

const onPickerSelect = (calendarType: string | null): void => {
  showCalendarTypePicker.value = false
  document.removeEventListener('pointerdown', onDocumentPointerDown, true)
  // Create mode: empty id + isNewCalendar flag, plus the chosen preset as the meetings's seed. The
  // CalendarSettingsMeetings mount watch then mounts the meetings MFA in create mode. onBeforeUnmount of this
  // table (unmounted by the v-else once advanced view flips on) tears down the picker island.
  AppState.selectedCalendarId = ''
  AppState.isNewCalendar = true
  AppState.selectedCalendarType = (calendarType as CalendarType) ?? null
  toggleAdvancedView()
}

onBeforeUnmount(() => {
  closeCalendarTypePicker()
})

const targetTimezone = getTargetTimezone()
const filterState = reactive({
  status: 'all',
  group: props.groupFilter,
  calendar_type: 'all',
  ownedBy: getOwnedBy() || 'anyone',
})

const tableActionsOptionsForActiveCalendars = [
  {
    label: t('settings.calendar.duplicate'),
    key: 'duplicate',
    icon: renderIcon(Copy07Icon),
  },
  {
    label: t('settings.calendar.move_to_group'),
    key: 'move_to_group',
    icon: renderIcon(ArrowRightIcon),
  },
  {
    label: t('settings.calendar.deactivate_calendar'),
    key: 'deactivate_calendar',
    icon: renderIcon(Toggle03LeftIcon),
  },
  {
    label: t('settings.calendar.delete_calendar'),
    key: 'delete_calendar',
    icon() {
      return h(renderIcon(Trash03Icon), { class: '!text-red-600' })
    },
    props: { class: 'border-t-[1px] !text-red-600' },
  },
]

const tableActionsOptionsForDisabledCalendars =
  tableActionsOptionsForActiveCalendars.filter(x => x)

tableActionsOptionsForDisabledCalendars[2] = {
  label: t('settings.calendar.activate_calendar'),
  key: 'deactivate_calendar',
  icon: renderIcon(Toggle03LeftIcon),
}

const columns = [
  {
    title: t('settings.calendar.calendar_name'),
    key: 'name',
    minWidth: 220,
    render: calendar => {
      return h(
        'div',
        { class: 'max-w-[220px] flex flex-col gap-1 items-start' },
        [
          h(
            'div',
            { class: 'flex max-w-[260px]' },
            h(
              'span',
              {
                id: 'name',
                class: 'truncate text-sm font-medium text-gray-700  text-left',
              },
              calendar?.name
            )
          ),
          h(
            UIButton,
            {
              class: 'cursor-pointer text-gray-400',
              text: true,
              onClick: () => {
                copyCalendarId(calendar.id)
              },
            },
            [
              h(
                'div',
                {
                  id: 'calendar-' + calendar.id,
                  class: 'text-[12px] font-normal text-gray-400',
                },
                `Id: ${calendar.id}`
              ),
              h(Copy04Icon, {
                class: 'h-3 w-3 ml-1',
              }),
            ]
          ),
        ]
      )
    },
  },
  {
    title: t('settings.calendar.group'),
    key: 'group',
    minWidth: 130,
    render(calendar) {
      return calendar.groupName
        ? h(
            'div',
            {
              class: 'inline-flex items-center rounded gap-2 max-w-[200px]',
            },
            [
              h(
                'h6',
                {
                  class: 'text-sm font-normal text-gray-500 w-[100%]',
                },
                calendar.groupName
              ),
            ]
          )
        : ''
    },
  },

  {
    title: t('settings.calendar.duration'),
    key: 'slotDuration',
    minWidth: 100,
    render(calendar) {
      const durationLabels = getCalendarDurationLabels(calendar)

      if (durationLabels.length > 1) {
        return h(
          'div',
          { class: 'inline-flex items-center gap-1', style: 'width: 120px' },
          [
            h('span', t('settings.calendar.multiple_durations')),
            h(
              UITooltip,
              { placement: 'top' },
              {
                trigger: () =>
                  h(InfoCircleIcon, {
                    class: 'h-4 w-4 shrink-0 text-gray-500',
                  }),
                default: () =>
                  h('div', { class: 'flex flex-col gap-1' }, [
                    h(
                      'span',
                      { class: 'font-medium' },
                      t('settings.calendar.duration')
                    ),
                    h(
                      'ul',
                      { class: 'list-disc pl-4 flex flex-col gap-1' },
                      durationLabels.map((label) => h('li', label))
                    ),
                  ]),
              }
            ),
          ]
        )
      }

      return durationLabels[0] ?? ''
    },
  },
  {
    title: t('settings.calendar.calendar_type'),
    key: 'calendarType',
    minWidth: 130,
    render(row) {
      return h(
        'div',
        {
          class: 'inline-flex items-center rounded gap-2 max-w-[110px]',
        },
        [
          row.calendarType === 'event'
            ? t('settings.calendar.event')
            : row.calendarType === 'class_booking'
            ? t('settings.calendar.class_booking')
            : row.calendarType === 'collective'
            ? t('labels.collective_booking')
            : row.calendarType === 'service_booking'
            ? t('labels.service_booking')
            : row.calendarType === 'round_robin'
            ? t('settings.calendar.round_robin')
            : t('settings.calendar.personal'),
        ]
      )
    },
  },
  {
    title: t('settings.calendar.status'),
    key: 'isActive',
    minWidth: 100,
    render(calendar) {
      return h('div', [
        h(
          UITag,
          {
            type: calendar.isActive ? 'success' : 'error',
            bordered: false,
            size: 'medium',
          },
          calendar.isActive
            ? t('settings.calendar.active')
            : t('settings.calendar.inactive')
        ),
      ])
    },
  },
  {
    title: t('settings.calendar.date_updated'),
    key: 'dateUpdated',
    minWidth: 150,
    render(calendar) {
      return h('div', { class: 'flex flex-col gap-1 items-start' }, [
        h(
          'div',
          {
            class: 'm-0 text-[13px] font-normal text-gray-600 ',
          },
          moment(calendar.dateUpdated).tz(targetTimezone)?.format('MMMM DD, YYYY')
        ),
        h(
          'div',
          {
            class: 'm-0 text-[12px] font-normal text-gray-400',
          },
          `${moment(calendar.dateUpdated)
            .tz(targetTimezone)
            ?.format('hh:mm A')}`
        ),
      ])
    },
  },
  {
    title: t('settings.calendar.actions'),
    key: 'actions',
    minWidth: 150,
    render(calendar) {
      const isCalendarsEditEnabled = isPermissionsEnabled(
        CalendarScopes.EDIT_CALENDARS
      )
      const isTourActive = showCalendarTour.value
      return h('div', { class: 'flex flex-row items-center' }, [
        isCalendarsEditEnabled &&
          h(CalendarActionTooltip, {
            parentId: `edit-calendar`,
            id: `edit-calendar-${calendar.id}`,
            icon: Edit01Icon,
            showTooltip: true,
            action: 'edit',
            tooltipText: t('settings.calendar.edit_calendar'),
            data: calendar,
            dropDownActionChange,
            customClass: isTourActive ? 'pl-[4px]' : '',
          }),
        h(CalendarActionTooltip, {
          parentId: `share-calendar`,
          id: `share-calendar-${calendar.id}`,
          icon: Share06Icon,
          showTooltip: true,
          action: 'share_calendar',
          tooltipText: t('settings.calendar.share_calendar'),
          data: calendar,
          dropDownActionChange,
          customClass: isTourActive ? 'pl-[4px]' : '',
        }),
        h(CalendarActionTooltip, {
          parentId: `troubleshoot-calendar`,
          id: `troubleshoot-calendar-${calendar.id}`,
          icon: Tool02Icon,
          showTooltip: true,
          action: 'troubleshoot_calendar',
          tooltipText: t('settings.calendar.troubleshoot_calendar_link'),
          data: calendar,
          dropDownActionChange,
          customClass: isTourActive ? 'pl-[4px]' : '',
        }),
        isCalendarsEditEnabled &&
          h(
            'div',
            { class: `ml-[-10px]` },
            h(CalendarActionTooltip, {
              parentId: `calendar-actions`,
              id: `calendar-actions-${calendar.id}`,
              showTooltip: true,
              tooltipText: t('settings.calendar.more_options'),
              data: calendar,
              isDropdown: true,
              dropDownActionChange,
              tableActionDropdownOptions: tableActionDropdownOptions(calendar),
            })
          ),
      ])
    },
  },
]

const tableActionDropdownOptions = (calendar: any) => {
  const options = calendar?.isActive
    ? tableActionsOptionsForActiveCalendars?.filter(x =>
        shouldShowLegacyLink(x.key, calendar)
      )
    : tableActionsOptionsForDisabledCalendars?.filter(x =>
        shouldShowLegacyLink(x.key, calendar)
      )

  if (!isPermissionsEnabled(CalendarScopes.EDIT_CALENDARS)) {
    return options.filter(
      x =>
        ![
          'duplicate',
          'deactivate_calendar',
          'move_to_group',
          'delete_calendar',
        ].includes(x.key)
    )
  }
  return options
}

const filterActionOptions = [
  {
    key: 'status',
    label: t('settings.calendar.status'),
    placeholder: t('settings.calendar.status'),
    value: '',
    options: [
      {
        label: t('settings.calendar.all'),
        key: 'all',
      },
      {
        label: t('settings.calendar.inactive'),
        key: 'draft',
      },
      {
        label: t('settings.calendar.active'),
        key: 'active',
      },
    ],
  },
  {
    key: 'calendar_type',
    label: t('settings.calendar.calendar_type'),
    placeholder: t('settings.calendar.calendar_type'),
    value: '',
    options: [
      {
        label: t('settings.calendar.all'),
        key: 'all',
      },
      {
        label: t('settings.calendar.round_robin'),
        key: CalendarType.ROUND_ROBIN,
      },
      {
        label: t('settings.calendar.event'),
        key: CalendarType.EVENT,
      },
      {
        label: t('settings.calendar.class_booking'),
        key: CalendarType.CLASS_BOOKING,
      },
      {
        label: t('labels.collective_booking'),
        key: CalendarType.COLLECTIVE,
      },
      {
        label: t('labels.service_booking'),
        key: CalendarType.SERVICE,
      },
      {
        label: t('settings.calendar.personal'),
        key: CalendarType.PERSONAL,
      },
    ].filter(
      option =>
        !(AppState.isCalendarTypeSplitEnabled || AppState.isServicesEnabled) ||
        option.key !== CalendarType.SERVICE
    ),
  },
  {
    key: 'ownedBy',
    value: 'me',
    label: t('settings.calendar.owned_by'),
    placeholder: t('settings.calendar.owned_by'),
    options: [
      {
        label: t('settings.calendar.owned_by_me'),
        key: 'me',
      },
      {
        label: t('settings.calendar.owned_by_anyone'),
        key: 'anyone',
      },
    ],
  },
]

const disableCalendar = async () => {
  if (!selectedCalender.value) {
    return
  }

  isProcessing.value = true

  await CalendarService.updateStatus(
    selectedCalender.value.id,
    !selectedCalender.value.isActive
  )

  await getCalendarsInLocation(true)
  updateCalendarStore()
  closeModal()
}

const deleteCalendar = async () => {
  isProcessing.value = true
  await CalendarService.delete(selectedCalender.value.id)

  await getCalendarsInLocation(true)
  updateCalendarStore()
  closeModal()
}

const duplicateCalendar = async () => {
  isProcessing.value = true

  const { data: fullCalendar } = await CalendarService.get(selectedCalender.value.id)
  if (fullCalendar?.calendar) {
    selectedCalender.value = fullCalendar.calendar
  }

  const fieldsToRemove = [
    'id',
    'syncToken',
    'version',
    'dateAdded',
    'providerId',
    'dateUpdated',
    'isActive',
    'deleted',
    'rrule',
    'slug',
    'groupName',
    'order',
    'unassignedLinkedCalendarId',
    'originId',
    'thanksMessage',
    'thankYouUrl',
    'onewaySyncAppointment',
    'createdBy',
    'lastUpdatedBy',
    'notificationStatus',
    'notifications',
  ]

  const fieldsToParseAsInt = [
    'slotDuration',
    'slotInterval',
    'slotBuffer',
    'preBuffer',
    'appoinmentPerSlot',
    'allowBookingAfter',
    'allowBookingFor',
  ]

  const payload = {
    ...selectedCalender.value,
  }

  fieldsToRemove.forEach(field => {
    delete payload[field]
  })

  fieldsToParseAsInt.forEach(field => {
    payload[field] = Number(payload[field])
  })

  payload.allowBookingAfterUnit = normalizeDurationUnitForApi(
    payload.allowBookingAfterUnit
  )

  if (selectedCalender.value.widgetSlug) {
    payload.widgetSlug = `${selectedCalender.value.widgetSlug}${generateSlug()}`
  }

  if (selectedCalender.value.teamMembers?.length === 0) {
    delete payload.teamMembers
  } else if (payload.teamMembers) {
    payload.teamMembers = payload.teamMembers.map(teamMember => ({
      ...teamMember,
      priority: Number(teamMember.priority),
    }))
  }

  payload.formSubmitRedirectURL = selectedCalender.value.formSubmitRedirectUrl
  delete payload.formSubmitRedirectUrl

  if (selectedCalender.value?.availabilities?.length > 0) {
    payload.availabilities = selectedCalender.value.availabilities.map(
      availability => {
        return {
          date: availability.date,
          hours: availability.hours,
        }
      }
    )
  }
  if (AppState.isScheduleEnabled) {
    payload.sourceCalendarId = selectedCalender.value.id
  }

  if (isEmpty(payload.openHours)) {
    delete payload.openHours
  }

  const {
    data: {
      calendar: { id },
    },
  } = await CalendarService.create({
    ...payload,
    name: `Copy of ${selectedCalender.value.name}`,
  })

  // duplicate event notifications
  try {
    const { data } = await EventNotificationService.getEventNotifications({
      altId: selectedCalender.value.id,
      altType: 'calendar',
    })

    // get default inApp notifications for the duplicated calendar
    const { data: duplicatedCalendarDefaultNotifications } =
      await EventNotificationService.getEventNotifications({
        altId: id,
        altType: 'calendar',
      })

    const channelNotificationMapping = {}
    const notifications: any[] = []
    data.forEach((e: any) => {
      if (
        !(
          e?.channel === 'inApp' &&
          ['booked', 'confirmation'].includes(e?.notificationType)
        )
      ) {
        notifications.push({
          ...e,
          altId: id,
          altType: 'calendar',
          _id: undefined, // remove mongo _id from the notification
        })
      } else {
        channelNotificationMapping[`${e?.channel}-${e?.notificationType}`] = e
      }
    })

    const updatedNotifications: any[] = []

    duplicatedCalendarDefaultNotifications.forEach((e: any) => {
      if (channelNotificationMapping[`${e?.channel}-${e?.notificationType}`]) {
        updatedNotifications.push({
          ...channelNotificationMapping[`${e?.channel}-${e?.notificationType}`],
          _id: e?._id,
          altId: id,
        })
      }
    })

    const promises: Promise<any>[] = []
    if (notifications.length) {
      promises.push(
        EventNotificationService.createEventNotifications(notifications)
      )
    }
    if (updatedNotifications.length) {
      promises.push(
        EventNotificationService.updateEventNotifications(updatedNotifications)
      )
    }

    if (promises.length) {
      await Promise.all(promises)
    }
  } catch (e) {
    console.error(`Error duplicating calendar: ${e}`)
  }

  //get calendar resources to duplicate
  if (selectedCalender.value.calendarType === CalendarType.SERVICE) {
    const calendarId = selectedCalender.value.id
    const rooms = AppState.globalRooms
    const roomsForNewCalendar =
      rooms
        ?.map(e => e)
        ?.filter((e: any) => e.calendarIds?.includes(calendarId) && e.isActive)
        ?.map((e: any) => e?._id) || []
    const equipments = AppState.globalEquipments
    const equipmentsForNewCalendar =
      equipments
        ?.map(e => e)
        ?.filter((e: any) => e.calendarIds?.includes(calendarId) && e.isActive)
        ?.map((e: any) => e?._id) || []
    const promises: Promise<any>[] = []
    roomsForNewCalendar?.forEach(resourceId => {
      promises.push(
        CalendarResourceService.update(
          resourceId,
          { calendarIds: [id] },
          CALENDAR_RESOURCE_TYPES.ROOMS
        )
      )
    })
    equipmentsForNewCalendar.forEach(resourceId => {
      promises.push(
        CalendarResourceService.update(
          resourceId,
          { calendarIds: [id] },
          CALENDAR_RESOURCE_TYPES.EQUIPMENTS
        )
      )
    })
    await Promise.all(promises)
    await getRoomsInLocation()
    await getEquipmentsInLocation()
  }

  if (selectedCalender.value.providerId) {
    await CalendarService.moveToGroup(
      AppState.locationId,
      selectedCalender.value.providerId,
      id
    )
  }

  if (selectedCalender.value.linkedCalendars?.google?.id) {
    await CalendarService.linkGoogleCalendar(id)
  }
  toggleAdvancedView()

  AppState.selectedCalendarId = id
  AppState.isNewCalendar = true

  closeModal()
}

const notification = useNotification()

function copyCalendarId(id) {
  copyToClipboard(id)
  sendNotification({
    type: 'info',
    title: t('settings.calendar.copied_to_clipboard'),
    icon: 'InfoCircleIcon',
    duration: 1000,
  })
}

function dropDownActionChange(action, row) {
  if (action === 'edit') {
    AppState.selectedCalendarId = row.id
    resetCalendarEditValidationData()
    toggleAdvancedView()
  } else if (action === 'duplicate') {
    selectedCalender.value = row
    showCalendarDuplicateConfirmationDialog.value = true
  } else if (action === 'move_to_group') {
    selectedCalender.value = row
    showMoveToTeamModal.value = true
    // openAddToGroupModal(row.id)
  } else if (action === 'delete_calendar') {
    selectedCalender.value = row
    showCalendarDeleteConfirmationDialog.value = true
    // deleteCalendar(calId)
  } else if (action === 'deactivate_calendar') {
    selectedCalender.value = row
    showDeactiveConfirmationModal.value = true
    // disableCalendar(row.id)
  } else if (action === 'share_calendar') {
    showSelectModal.value = true
    calendarRef.value = row
  } else if (action === 'troubleshoot_calendar') {
    calendarRef.value = row
    AppState.parentApp?.then(parent => {
      parent.emit('open-troubleshoot-calendar', {
        calendarId: row.id,
      })
    })
  }
}

const closeShareCalendarPopup = () => {
  showSelectModal.value = false
}

const closeModal = () => {
  selectedCalender.value = {}
  showDeactiveConfirmationModal.value = false
  showCalendarDeleteConfirmationDialog.value = false
  showCalendarDuplicateConfirmationDialog.value = false
  showMoveToTeamModal.value = false
  isProcessing.value = false
}

const moveSelectedCalToGroup = async selectedTeam => {
  isProcessing.value = true
  const { data } = await CalendarService.moveToGroup(
    AppState.locationId,
    selectedTeam,
    selectedCalender.value.id
  )
  await Promise.all([getCalendarsInLocation(true), getTeamsInLocation(true)])
  closeModal()
  isProcessing.value = false
  if (data.hasCalendarSlugChanged) {
    notification.info({
      content: 'The slug for this calendar was changed to avoid conflicts',
      duration: 3 * 1000,
    })
  }
}

onMounted(async () => {
  await fetchUserDetails(AppState?.user?.id, true)
  AppState.showCalendarLinkModal = false
  getOwnedBy()
})

async function onUpdateFilter(value) {
  if (value?.filters?.length) {
    calendarList.filters.value.status =
      value?.filters?.find(x => x.key === 'status')?.value || 'all'
    calendarList.filters.value.group =
      value?.filters?.find(x => x.key === 'group')?.value || ''
    calendarList.filters.value.calendar_type =
      value?.filters?.find(x => x.key === 'calendar_type')?.value || ''
    calendarList.filters.value.ownedBy =
      value?.filters?.find(x => x.key === 'ownedBy')?.value || 'me'
  } else {
    calendarList.filters.value.status = 'all'
    calendarList.filters.value.group = ''
    calendarList.filters.value.calendar_type = ''
    calendarList.filters.value.ownedBy = 'me'
  }
  calendarList.filters.value.page = 1
}

const setOwnedBy = (value: string) => {
  calendarList.filters.value.ownedBy = value
  filterState.ownedBy = value
  const type = props.type
  const key = `calendar_settings_calendar_filter_ownedBy_${locationId}_${userId}_${type}`
  localStorage.setItem(key, value)
}

function handleFilterChange(key, value) {
  filterState[key] = value
  calendarList.filters.value[key] = value
  calendarList.filters.value.page = 1
  if (key === 'ownedBy') {
    setOwnedBy(value)
  }
}

const checkedRowKeysRef = ref([])
const handleCheck = rowKeys => {
  checkedRowKeysRef.value = rowKeys
}

const selectedCalender = ref()
const showDeactiveConfirmationModal = ref(false)
const showCalendarDeleteConfirmationDialog = ref(false)
const showCalendarDuplicateConfirmationDialog = ref(false)
const isProcessing = ref(false)
const showMoveToTeamModal = ref(false)

watch(
  () => props.groupFilter,
  newValue => {
    filterState.group = newValue || ''
    calendarList.filters.value.group = newValue || ''
  }
)

const onClickEndTour = (step: number) => {
  resetLocationChanged()
  if (!AppState.exitTour) {
    const onboardingData = [
      {
        key: 'calendar-settings',
        isCompleted: step === 4,
        show: false,
        exitStep: step,
      },
    ]
    const payload = {
      locationId: AppState.locationId,
      userId: AppState.loggedInUserDetail?.id,
      calendarOnboarding: onboardingData,
    }
    updateCalendarOnboardingStatus(payload)
    getCalendarOnboardingStatus()
  }
}

const showCalendarTour = computed(() => {
  const tourInfo = AppState.calendarsOnboarding.find(
    x => x.key === 'calendar-settings'
  )
  const isTourAllowed = tourInfo?.show ?? true
  const hasCalendars = calendarList?.calendarList?.length > 0
  const isEligibleForTour =
    AppState.hasLocationChanged &&
    !AppState.onboardingError &&
    AppState.isUserPartOfLocation &&
    !AppState.showCalendarLinkModal &&
    !AppState.showAdvancedCalendarSettings &&
    !AppState.isLoading

  // @ts-expect-error: driver_frame is not defined in the window object
  const onboardingTourRunning = window?.driver_frame?.isActive() || false
  return (
    isTourAllowed && hasCalendars && isEligibleForTour && !onboardingTourRunning
  )
})

watch(
  () => showCalendarTour.value,
  newValue => {
    if (newValue) {
      nextTick(() => {
        const tableContainer = document.querySelector('[id^="edit-calendar"]')
        if (tableContainer) {
          tableContainer.scrollIntoView({ behavior: 'smooth', block: 'end' })
        }
      })
    }
  }
)

const selectedCalendarData = computed(() => {
  const cal = calendarRef.value
  const data: ICalendarShare = {
    id: cal?.id,
    slug: cal?.widgetSlug,
    name: cal?.name,
    calendarType: cal?.calendarType,
    providerId: cal?.providerId,
    slotDuration: cal?.slotDuration,
    slotDurationUnit: cal?.slotDurationUnit,
    widgetType: cal?.widgetType,
    source: SHARE_SOURCE.CALENDAR,
    schedulePath: 'bookings',
    permanentPath: 'booking',
    otlPath: 'otl',
    showOneTimeLink: true,
  }
  return data
})

const showGroupCalendarButton = computed(() => {
  return !isCreateCalendarDisabled.value && !isCreateGroupDisabled.value
})

const tourSteps = computed(() => {
  return getCalendarTourSteps(!isCreateCalendarDisabled.value, t)
})

const onSearch = (value: string) => {
  calendarList.filters.value.searchText = value
  calendarList.filters.value.page = 1
  groupList.filters.value.searchText = value
  groupList.filters.value.page = 1
}
</script>
<template>
  <div id="calendarsListContainer">
    <Tour
      v-if="showCalendarTour"
      :steps="tourSteps"
      :auto-start="true"
      :restart-on-last-prev="true"
      :exit-tour="AppState.exitTour"
      @click-end-tour="onClickEndTour"
    />
    <UITable
      v-if="calendarList"
      id="calendars-list-table"
      :columns="columns"
      :scroll-x="980"
      :data="calendarList?.calendarList || []"
      :page="calendarList.filters.value.page"
      :page-count="calendarList?.nosTotalPages || 0"
      :loading="AppState.loadingAssets.calendars"
      :row-key="cal => cal.id"
      :enable-text-search="false"
      :transparent="true"
      @update:checked-row-keys="handleCheck"
      @update:page="x => (calendarList.filters.value.page = x)"
      @update:filter="onUpdateFilter"
    >
      <template #empty>
        <div v-if="calendarList.calendarList.length === 0">
          <EmptyState
            id="calendars"
            :image="add_new"
            :title="
              isCreateCalendarDisabled
                ? t('settings.calendar.no_calendars_yet')
                : t('settings.calendar.no_calendars_added')
            "
            :description="
              isCreateCalendarDisabled
                ? t('settings.calendar.no_calendars_access')
                : t('settings.calendar.no_calendars_added_description')
            "
            :primary-button-text="t('settings.calendar.create_calendar')"
            :show-button="showGroupCalendarButton"
            :on-primary-button-click="startCalendarCreation"
          />
        </div>

        <UIEmpty
          v-else
          id="No-calendars-found-2"
          :title="t('settings.calendar.no_calendars_found_title')"
          :description="t('settings.calendar.no_calendars_found_search')"
        />
      </template>
      <template #filter-left-start>
        <FilterItem
          id="calendar-status-filter"
          :filter-options="filterActionOptions[0].options"
          :on-change="handleFilterChange"
          :filter-value="filterState.status"
          :placeholder="filterActionOptions[0].placeholder"
          :label="filterActionOptions[0].label"
          :filter-key="filterActionOptions[0].key"
        />
        <FilterItem
          v-if="type === 'sales'"
          id="calendar-type-filter"
          :filter-options="filterActionOptions[1].options"
          :on-change="handleFilterChange"
          :filter-value="filterState.calendar_type"
          :placeholder="filterActionOptions[1].placeholder"
          :label="filterActionOptions[1].label"
          :filter-key="filterActionOptions[1].key"
        />
        <FilterItem
          v-if="!assignedDataOnly"
          id="calendar-owned-by-filter"
          :filter-options="filterActionOptions[2].options"
          :on-change="handleFilterChange"
          :filter-value="filterState.ownedBy"
          :placeholder="filterActionOptions[2].placeholder"
          :label="filterActionOptions[2].label"
          :filter-key="filterActionOptions[2].key"
        />
      </template>
      <template #filter-right-end>
        <SearchBox
          :search-text="calendarList.filters.value.searchText"
          :on-search="onSearch"
          :placeholder="t('settings.calendar.calendar_group_name')"
          class="w-full"
        />
        <div class="relative flex flex-row items-center gap-2">
          <UITooltip :trigger="isCreateCalendarDisabled ? 'hover' : 'manual'">
            <template #trigger>
              <!-- No @keydown handler: UIButton renders a native <button>, so Enter/Space already
                   dispatch a click. Handling keydown as well would call this toggle twice per key
                   press (open then immediately close) and fire it on Tab and arrow keys too. -->
              <UIButton
                :id="NEW_CALENDAR_BUTTON_ID"
                type="primary"
                :disabled="isCreateCalendarDisabled"
                size="small"
                @click="startCalendarCreation"
              >
                <div class="flex flex-row items-center gap-2">
                  <PlusIcon class="h-5 w-5 text-white" />
                  <div class="font-normal">
                    {{ t('settings.calendar.new_calendar') }}
                  </div>
                </div>
              </UIButton>
            </template>
            <div class="max-w-[300px]">
              {{ t('settings.calendar.create_calendar_access_disabled') }}
            </div>
          </UITooltip>
          <!-- Calendar-type picker island (MF remote), anchored as a dropdown under the button. -->
          <div
            v-show="showCalendarTypePicker"
            :id="PICKER_MOUNT_ID"
            ref="pickerMountRef"
            class="absolute right-0 top-full z-[60] mt-2"
          />
        </div>
      </template>
    </UITable>

    <GeneralConfirmation
      v-model:show="showDeactiveConfirmationModal"
      :disabled-confirm="isProcessing"
      :header-title="
        t('settings.calendar.calendar_status_message_title', {
          status: selectedCalender?.isActive ? 'Deactivate' : 'Activate',
        })
      "
      @on-confirm="disableCalendar"
      @on-modal-close="closeModal"
    >
      <template #message>
        <p class="text-sm leading-5 text-gray-500">
          {{
            t('settings.calendar.calendar_status_message', {
              status: selectedCalender?.isActive ? 'deactivate' : 'activate',
            })
          }}
        </p>
      </template>
    </GeneralConfirmation>

    <SelectMoveTeamModal
      :show="showMoveToTeamModal"
      :current-team="selectedCalender?.providerId"
      :disabled-confirm="isProcessing"
      @on-confirm="moveSelectedCalToGroup"
      @on-modal-close="closeModal"
    />

    <DeleteConfirmation
      v-model:show="showCalendarDeleteConfirmationDialog"
      :disabled-confirm="isProcessing"
      :header-title="t('settings.calendar.delete_calendar_message_title')"
      @on-confirm="deleteCalendar"
      @on-modal-close="closeModal"
    >
      <template #message>
        <p class="text-sm leading-5 text-gray-500">
          {{ t('settings.calendar.delete_calendar_message') }}
        </p>
      </template>
    </DeleteConfirmation>

    <DuplicateConfirmation
      v-model:show="showCalendarDuplicateConfirmationDialog"
      :disabled-confirm="isProcessing"
      :header-title="t('settings.calendar.duplicate_calendar_message_title')"
      @on-confirm="duplicateCalendar"
      @on-modal-close="closeModal"
    >
      <template #message>
        <p class="text-sm leading-5 text-gray-500">
          {{ t('settings.calendar.duplicate_calendar_message') }}
        </p>
      </template>
    </DuplicateConfirmation>
    <CalendarShareModal
      v-if="showSelectModal"
      :show="showSelectModal"
      :data="selectedCalendarData"
      @close-modal="closeShareCalendarPopup"
    />
  </div>
</template>

<style lang="scss">
#calendarsListContainer {
  // As a flex child (w-full beside the 250px groups panel) this defaults to
  // min-width:auto and refuses to shrink below the table's min-content width,
  // which pushes the whole page wide. min-width:0 lets it shrink to the
  // available space so the table's own scroll-x scrolls internally instead.
  min-width: 0;

  .table-container .n-data-table .n-data-table-td {
    padding: 10px 24px !important;
  }
}
</style>
